(function initializeLoveSquishyCartRequest() {
  if (window.LSCartRequest) return;

  const retryDelays = [1800];
  const minimumRequestGap = 450;
  const challengeCooldown = 4000;
  let requestQueue = Promise.resolve();
  let nextRequestAt = 0;

  const wait = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

  function header(response, name) {
    return response?.headers?.get?.(name) || '';
  }

  function isChallengeResponse(response, contentType = '') {
    return (
      response?.status === 429 ||
      header(response, 'cf-mitigated').toLowerCase() === 'challenge' ||
      (!response?.ok && contentType.toLowerCase().includes('text/html'))
    );
  }

  function isRetryableResponse(response, contentType = '') {
    return (
      isChallengeResponse(response, contentType) ||
      [500, 502, 503, 504].includes(response?.status)
    );
  }

  function retryDelay(response, fallback) {
    const retryAfter = header(response, 'retry-after').trim();
    let delay = Number.NaN;

    if (/^\d+(?:\.\d+)?$/.test(retryAfter)) {
      delay = Number.parseFloat(retryAfter) * 1000;
    } else if (retryAfter) {
      delay = Date.parse(retryAfter) - Date.now();
    }

    if (!Number.isFinite(delay) || delay <= 0) delay = fallback;
    return Math.min(Math.max(delay, 500), 5000);
  }

  function cartRequestError(message, { status = 0, code = 'SERVICE_UNAVAILABLE', isChallenge = false } = {}) {
    return Object.assign(new Error(message), {
      status,
      code,
      isCartChallenge: isChallenge,
    });
  }

  function validAddedItem(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const variantId = item.variant_id ?? item.id;
    return /^[1-9]\d*$/.test(String(variantId)) && Number.isSafeInteger(Number(variantId)) &&
      Number.isInteger(Number(item.quantity)) && Number(item.quantity) > 0;
  }

  async function executeJsonRequest(url, config, options = {}) {
    const configuredDelays = Array.isArray(options.retryDelays) ? options.retryDelays : retryDelays;
    const isCartAdd = /\/cart\/add(?:\.js|\.json)?(?:[?#]|$)/.test(String(url));
    const delays = isCartAdd ? [] : configuredDelays;
    const fetchImplementation = options.fetchImplementation || window.fetch.bind(window);
    const waitImplementation = options.waitImplementation || wait;

    for (let attempt = 0; attempt <= delays.length; attempt += 1) {
      const slotDelay = Math.max(0, nextRequestAt - Date.now());
      if (slotDelay > 0) await waitImplementation(slotDelay);

      let response;
      let responseText = '';

      try {
        response = await fetchImplementation(url, config);
        responseText = await response.text();
      } catch (networkError) {
        if (isCartAdd) throw cartRequestError(window.cartStrings?.add_uncertain || 'Check your cart before adding this item again.', { code: 'CART_ADD_UNCERTAIN' });
        if (attempt < delays.length) {
          const delay = delays[attempt];
          nextRequestAt = Date.now() + delay;
          await waitImplementation(delay);
          nextRequestAt = 0;
          continue;
        }

        nextRequestAt = Date.now() + minimumRequestGap;
        throw cartRequestError(networkError?.message || window.cartStrings?.error || 'Cart request failed.');
      }

      const contentType = header(response, 'content-type');
      const challenge = isChallengeResponse(response, contentType);
      if (isCartAdd && isRetryableResponse(response, contentType)) {
        nextRequestAt = Date.now() + (challenge ? challengeCooldown : minimumRequestGap);
        throw cartRequestError(window.cartStrings?.add_uncertain || 'Check your cart before adding this item again.', { status: response.status, code: 'CART_ADD_UNCERTAIN', isChallenge: challenge });
      }
      if (isRetryableResponse(response, contentType) && attempt < delays.length) {
        const delay = retryDelay(response, delays[attempt]);
        nextRequestAt = Date.now() + delay;
        await waitImplementation(delay);
        nextRequestAt = 0;
        continue;
      }

      if (challenge) {
        nextRequestAt = Date.now() + challengeCooldown;
        throw cartRequestError(window.cartStrings?.busy || window.cartStrings?.error || 'Cart is busy.', {
          status: response.status,
          code: 'CART_BUSY',
          isChallenge: true,
        });
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (_parseError) {
        nextRequestAt = Date.now() + minimumRequestGap;
        throw cartRequestError(window.cartStrings?.error || 'Cart request failed.', {
          status: response.status,
          code: isCartAdd ? 'CART_ADD_UNCERTAIN' : 'SERVICE_UNAVAILABLE',
        });
      }

      nextRequestAt = Date.now() + minimumRequestGap;
      if (isCartAdd && response.ok) {
        const addedItems = Array.isArray(data?.items) ? data.items : [data];
        if (!addedItems.length || !addedItems.every(validAddedItem)) {
          throw cartRequestError(window.cartStrings?.add_uncertain || 'Check your cart before adding this item again.', { status: response.status, code: 'CART_ADD_UNCERTAIN' });
        }
      }
      return { response, data };
    }

    throw cartRequestError(window.cartStrings?.error || 'Cart request failed.');
  }

  function json(url, config = {}, options = {}) {
    const queuedRequest = requestQueue
      .catch(() => undefined)
      .then(() => executeJsonRequest(url, config, options));
    requestQueue = queuedRequest.then(
      () => undefined,
      () => undefined
    );
    return queuedRequest;
  }

  function itemSignature(item) {
    const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    if (properties.some(([, value]) => value !== null && !['string', 'number', 'boolean'].includes(typeof value))) return null;
    return JSON.stringify([
      String(item.variant_id || item.id || ''),
      String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
      properties.map(([key, value]) => [key, value == null ? '' : String(value)]),
    ]);
  }

  function cartQuantities(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.currency) return null;
    const map = new Map();
    let count = 0;
    for (const item of cart.items) {
      const quantity = Number(item.quantity);
      const signature = itemSignature(item);
      if (!signature || !Number.isInteger(quantity) || quantity < 1) return null;
      map.set(signature, (map.get(signature) || 0) + quantity);
      count += quantity;
    }
    return count === cart.item_count ? map : null;
  }

  function requestedItems(body) {
    try {
      if (body && typeof body.get === 'function') {
        const properties = {};
        for (const [key, value] of body.entries()) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match) properties[match[1]] = value;
        }
        return [{ id: body.get('id'), quantity: Number(body.get('quantity') || 1), selling_plan: body.get('selling_plan') || '', properties }];
      }
      const parsed = typeof body === 'string' ? JSON.parse(body) : body;
      return parsed?.items || (parsed?.id ? [parsed] : null);
    } catch {
      return null;
    }
  }

  function confirmsAddition(before, after, items) {
    if (!before || !after || before.currency !== after.currency || !items?.length) return false;
    if (before.token && after.token && before.token !== after.token) return false;
    const expected = cartQuantities(before);
    const actual = cartQuantities(after);
    if (!expected || !actual) return false;
    for (const item of items) {
      const quantity = Number(item.quantity || 1);
      const signature = itemSignature(item);
      if (!item.id || !signature || !Number.isInteger(quantity) || quantity < 1) return false;
      expected.set(signature, (expected.get(signature) || 0) + quantity);
    }
    return expected.size === actual.size && [...expected].every(([key, value]) => actual.get(key) === value);
  }

  async function readCart(options) {
    try {
      const result = await json(`${window.routes?.cart_url || '/cart'}.js`, { method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store' }, options);
      return result.response.ok && cartQuantities(result.data) ? result : null;
    } catch {
      return null;
    }
  }

  async function add(url, config = {}, options = {}) {
    const items = requestedItems(config.body);
    const before = await readCart(options);
    try {
      return await json(url, config, options);
    } catch (error) {
      if (error.code !== 'CART_ADD_UNCERTAIN') throw error;
      const after = await readCart(options);
      if (confirmsAddition(before?.data, after?.data, items)) {
        return { ...after, recovered: true };
      }
      error.cartReadback = after?.data || null;
      throw error;
    }
  }

  window.LSCartRequest = {
    json,
    add,
    isChallengeResponse,
    isRetryableResponse,
    retryDelay,
  };
})();
